// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
import ProductTextBadges, { ProductTextBadgesStyles } from "@insite/content-library/Components/ProductTextBadges";
import ProductListProductImage, {
    ProductListProductImageStyles,
} from "@insite/content-library/Widgets/ProductList/ProductListProductImage";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductActions, { ProductActionsProps, ProductActionsStyle } from "@synd/App/Components/Product/ProductActions";
import SimilarItemsButton from "@synd/App/Components/Product/SimilarItemsButton";
import ProductGridContainer, {
    ProductGridContainerStyle,
} from "@synd/App/Components/ProductGridCard/ProductGridContainer";
import { CustomAppState } from "@synd/App/Store/Reducers";
import { xIsAuthenticated } from "@synd/App/Store/Selectors/GeneralSelectors";
import ProductPrice, { ProductPriceStyles } from "@synd/Overrides/Components/ProductPrice";
import { mergeWithCss, parseBoolean } from "@synd/XDL/Utilities";
import * as React from "react";
import { FC, useState } from "react";
import { connect } from "react-redux";
export interface ProductPriceAndActionsProps extends Omit<ProductActionsProps, "extendedStyles"> {
    showAddToList?: boolean;
    showAvailability?: boolean;
    showPrice?: boolean;
    showTextBadges?: boolean;
    showAddToCartActions?: boolean;
    showCompare?: boolean;
    maximumNumberOfTextBadges?: number;
    showImageBadges?: boolean;
    maximumNumberOfImageBadges?: number;
    extendedStyles?: ProductPriceAndActionsStyle;
    onAddToCartClick?: () => void;
    showDisabled?: boolean;
    showAddToCartIcon?: boolean;
    containerClassName?: string;
    isPdp?: boolean;
    onChangeCompare?: (checked: boolean) => void;
    showOriginPrice?: boolean;
    preserveSpaceForAvailability?: boolean;
    isTableView?: boolean;
    isGridView?: boolean;
    showInventoryPlaceHolder?: boolean;
    isLoading?: boolean;
    showAddToCartDivider?: boolean;
    relatedProductType?: string;
    anchorId?: string;
    isAddToListDisabled?: boolean;
}
const mapStateToProps = (state: CustomAppState) => ({
    canSeePrices: getSettingsCollection(state).productSettings.canSeePrices,
    isMobileScreen: state.xCommon.isMobileScreen,
    isAuthenticated: xIsAuthenticated(state),
});

type Props = ProductPriceAndActionsProps & HasProductContext & ReturnType<typeof mapStateToProps>;

export interface ProductPriceAndActionsStyle {
    productGridContainer?: ProductGridContainerStyle;
    productActionsStyles?: ProductActionsStyle;
    productPriceStyles?: ProductPriceStyles;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    productAddToListStyles?: ProductAddToListLinkStyles;
    productListProductImageStyles?: ProductListProductImageStyles;
    textBadgesStyles?: ProductTextBadgesStyles;
    bottomSectionContainer?: InjectableCss;
    productDetailsQtyBreakPricing?: InjectableCss;
    prebookWrapper?: InjectableCss;
    prebookLabel?: TypographyPresentationProps;
    prebookMessage?: TypographyPresentationProps;
    multipleSalesQtyMsg?: TypographyPresentationProps;
    borderTop?: InjectableCss;
    variantsWrapper?: InjectableCss;
}

export const productPriceAndActionsStyle: ProductPriceAndActionsStyle = {};

const ProductPriceAndActions: FC<Props> = ({
    showAddToList = true,
    showAvailability = true,
    showPrice = true,
    showTextBadges,
    extendedStyles,
    showAddToCartActions = true,
    showAddToCartIcon = false,
    showCompare = false,
    maximumNumberOfTextBadges = 4,
    productContext,
    canSeePrices,
    onAddToCartClick,
    showDisabled,
    containerClassName,
    isPdp,
    onChangeCompare,
    showOriginPrice = true,
    maximumNumberOfImageBadges = 4,
    showImageBadges,
    isMobileScreen,
    preserveSpaceForAvailability = false,
    showInventoryPlaceHolder = false,
    isTableView = false,
    isGridView = false,
    showAddToCartDivider = false,
    anchorId,
    relatedProductType,
    isAuthenticated,
    isAddToListDisabled = false,
    ...productActionsProps
}) => {
    const [styles] = useState(() => mergeWithCss(productPriceAndActionsStyle, extendedStyles));

    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });
    const multipleSalesQty = productContext.product.detail?.multipleSaleQty;
    return (
        <ProductGridContainer
            data-test-selector="product-actions-grid-template"
            extendedStyles={styles.productGridContainer}
            className={containerClassName}
        >
            <ProductGridContainer.ExtraContent>
                <StyledWrapper {...styles.bottomSectionContainer}>
                    {((isGridView && showAvailability) ||
                        (showTextBadges && !!textBadges?.length) ||
                        (showImageBadges && !!imageBadges?.length)) && (
                        <ProductGridContainer.Availability {...{ isGridView }}>
                            {isGridView && showAvailability && (
                                <ProductContextAvailability
                                    extendedStyles={styles.productAvailabilityStyles}
                                    isProductDetailsPage={isPdp}
                                />
                            )}
                            {showTextBadges && !!textBadges?.length && (
                                <ProductTextBadges
                                    badgeList={textBadges}
                                    maxBadges={maximumNumberOfTextBadges}
                                    extendedStyles={styles.textBadgesStyles}
                                />
                            )}
                            {showImageBadges && !!imageBadges?.length && (
                                <ProductImageBadges badgeList={imageBadges} maxBadges={maximumNumberOfImageBadges} />
                            )}
                        </ProductGridContainer.Availability>
                    )}
                    {isPdp && !isAuthenticated && (
                        <StyledWrapper>
                            {createWidgetElement("ProductDetails/ProductDetailsVariantOptions", {
                                fields: {},
                            })}
                        </StyledWrapper>
                    )}

                    {showPrice && (
                        <ProductGridContainer.Price>
                            <ProductPrice
                                product={productContext}
                                showLabel={false}
                                showPack={false}
                                extendedStyles={styles.productPriceStyles}
                                showSalePriceLabel={!isPdp}
                                showSavings={showOriginPrice}
                                // XNG-Changes
                                displayVariantParentPlaceholder={isPdp}
                            />
                        </ProductGridContainer.Price>
                    )}
                    {!isGridView && canSeePrices && showAvailability && (
                        <ProductGridContainer.Availability {...{ isTableView }}>
                            <ProductContextAvailability
                                extendedStyles={styles.productAvailabilityStyles}
                                isProductDetailsPage={isPdp}
                            />
                        </ProductGridContainer.Availability>
                    )}
                    {isPdp && (
                        <>
                            {isAuthenticated && (
                                <>
                                    <StyledWrapper {...styles.variantsWrapper}>
                                        {createWidgetElement("ProductDetails/ProductDetailsVariantOptions", {
                                            fields: {},
                                        })}
                                    </StyledWrapper>
                                    <StyledWrapper {...styles.productDetailsQtyBreakPricing}>
                                        {createWidgetElement("ProductDetails/ProductDetailsQuantityBreakPricing", {
                                            fields: {},
                                        })}
                                    </StyledWrapper>
                                </>
                            )}
                            <SimilarItemsButton anchorId={anchorId} relatedProductType={relatedProductType} />
                            {isAuthenticated && parseBoolean(productContext.product.properties.isPrebook) && (
                                <StyledWrapper {...styles.prebookWrapper}>
                                    <Typography {...styles.prebookLabel}>
                                        {siteMessage("Custom_Prebook_label")}
                                    </Typography>
                                    <Typography {...styles.prebookMessage}>
                                        {siteMessage("Custom_Prebook_Message")}
                                    </Typography>
                                </StyledWrapper>
                            )}
                            {isAuthenticated && !!multipleSalesQty && multipleSalesQty > 1 && (
                                <StyledWrapper {...styles.borderTop}>
                                    <Typography {...styles.multipleSalesQtyMsg}>
                                        {siteMessage(
                                            "Custom_PDP_MultipleSalesQty",
                                            `${multipleSalesQty}`,
                                            `${multipleSalesQty}`,
                                            `${multipleSalesQty}`,
                                            `${multipleSalesQty + 1}`,
                                            `${multipleSalesQty * 2}`,
                                        )}
                                    </Typography>
                                </StyledWrapper>
                            )}
                        </>
                    )}
                    {showAddToCartActions && (
                        <ProductGridContainer.AddToCartActions {...{ showDivider: showAddToCartDivider }}>
                            <ProductActions
                                {...productActionsProps}
                                onAddToCartClick={onAddToCartClick}
                                extendedStyles={styles.productActionsStyles}
                                isPDPActions={isPdp}
                            />
                        </ProductGridContainer.AddToCartActions>
                    )}
                    {(showAddToList || showCompare) && (
                        <ProductGridContainer.AddToList>
                            {showCompare && (
                                <ProductListProductImage
                                    showImage={false}
                                    showCompare={showCompare}
                                    extendedStyles={styles.productListProductImageStyles}
                                />
                            )}
                            {showAddToList && (
                                <ProductAddToListLink
                                    extendedStyles={styles.productAddToListStyles}
                                    {...{ isDisabled: isAddToListDisabled }}
                                />
                            )}
                        </ProductGridContainer.AddToList>
                    )}
                </StyledWrapper>
            </ProductGridContainer.ExtraContent>
        </ProductGridContainer>
    );
};

export default connect(mapStateToProps)(withProductContext(ProductPriceAndActions));
